import React, { useEffect, useState, Fragment } from "react";
import axios from "axios";
import { API_BASE_URL, buildApiUrl } from "../config/api";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Table,
  Form,
  FormGroup,
  Input,
  Button,
} from "reactstrap";
import "../assets/css/style.css";

const emptyQRState = {
  title: "",
  description: "",
  qr_code_url: "",
  name: "",
  title_ar: "",
  description_ar: "",
  name_ar: "",
  link_url_en: "",
  link_url_ar: "",
  embed_video_url: "",
  multi_images: [],
  existing_multi_images: [],
  new_multi_images: [],
  qr_image: null,
  existing_qr_image: "",
  remove_qr_image: false,
  image: null,
  audio: null,
  video: null,
  logo: null,
};

const resolveMediaUrl = (value) => (value ? buildApiUrl(value) : "");
const fileNameFromPath = (value) => String(value || "").split("/").filter(Boolean).pop() || "download";
const buildUpdateQRState = (qr = {}) => ({
  ...emptyQRState,
  title: qr.title || "",
  description: qr.description || "",
  qr_code_url: qr.qr_code_url || "",
  name: qr.name || "",
  title_ar: qr.title_ar || "",
  description_ar: qr.description_ar || "",
  name_ar: qr.name_ar || "",
  link_url_en: qr.link_url_en || "",
  link_url_ar: qr.link_url_ar || "",
  embed_video_url: qr.embed_video_url || "",
  existing_multi_images: Array.isArray(qr.multi_images) ? qr.multi_images : [],
  new_multi_images: [],
  existing_qr_image: qr.qr_image || "",
  qr_image: null,
  remove_qr_image: false,
});

function QRManagement() {
  const [qrcodes, setQRcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ====== NEW: add form state & messages ======
  const [newQR, setNewQR] = useState({ ...emptyQRState });
  const [addMsg, setAddMsg] = useState({ ok: "", err: "" });

  // ====== Update form state ======
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [selectedQRId, setSelectedQRId] = useState(null);
  const [updateQR, setUpdateQR] = useState({ ...emptyQRState });
  const [qrCodeCount, setQRCodeCount] = useState(0);

  // validation messages (shared)
  const [imageError, setImageError] = useState("");
  const [multiImagesError, setMultiImagesError] = useState("");
  const [qrImageError, setQrImageError] = useState("");
  const [audioError, setAudioError] = useState("");
  const [videoError, setVideoError] = useState("");
  const [logoError, setLogoError] = useState("");
  const [updateQRSuccessMessage, setUpdateQRSuccessMessage] = useState("");
  const [updateError, setUpdateError] = useState({
    titleError: "",
    descriptionError: "",
    nameError: "",
    generalError: "",
  });
  const [expandedDescriptionId, setExpandedDescriptionId] = useState(null);

  const toggleDescription = (qrId) => {
    setExpandedDescriptionId(expandedDescriptionId === qrId ? null : qrId);
  };

  // ====== validators ======
  const isValidImageExtension = (fileName) => {
    const valid = [".webp", ".gif", ".png", ".jpg", ".jpeg"];
    return valid.includes(`.${fileName.split(".").pop().toLowerCase()}`);
  };
  const isValidAudioExtension = (fileName) => {
    const valid = [".alac", ".wma", ".m4a", ".ogg", ".flac", ".aac", ".mp3", ".wav"];
    return valid.includes(`.${fileName.split(".").pop().toLowerCase()}`);
  };
  const isValidVideoExtension = (fileName) => {
    const valid = [".ogg", ".3gp", ".mpeg", ".mpg", ".m4v", ".webm", ".flv", ".wmv", ".avi", ".mov", ".mp4"];
    return valid.includes(`.${fileName.split(".").pop().toLowerCase()}`);
  };

  // ====== handlers: add form ======
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setNewQR((prev) => ({ ...prev, [name]: value }));
  };

  const downloadFile = async (filePath) => {
    const normalizedUrl = resolveMediaUrl(filePath);
    if (!normalizedUrl) return;

    try {
      const response = await axios.get(normalizedUrl, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileNameFromPath(filePath);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download file:", err);
    }
  };

  const handleFileChange = (e, fieldName, isUpdate = false) => {
    const files = Array.from(e.target.files || []);
    const file = files[0];
    if (!files.length) return;

    let ok = false;
    switch (fieldName) {
      case "image":
      case "logo":
      case "qr_image":
        ok = isValidImageExtension(file.name);
        if (!ok) {
          const msg = `Invalid file extension for ${fieldName}. Allowed: .WEBP, .GIF, .PNG, .JPG, .JPEG`;
          if (fieldName === "image") setImageError(msg);
          else if (fieldName === "logo") setLogoError(msg);
          else setQrImageError(msg);
        }
        break;
      case "multi_images":
        ok = files.every((currentFile) => isValidImageExtension(currentFile.name));
        if (!ok) {
          setMultiImagesError("Invalid file extension for multi images. Allowed: .WEBP, .GIF, .PNG, .JPG, .JPEG");
        }
        break;
      case "audio":
        ok = isValidAudioExtension(file.name);
        if (!ok) setAudioError("Invalid file extension for audio. Allowed: .alac, .wma, .m4a, .ogg, .flac, .aac, .mp3, .wav");
        break;
      case "video":
        ok = isValidVideoExtension(file.name);
        if (!ok) setVideoError("Invalid file extension for video. Allowed: .ogg, .3gp, .mpeg, .mpg, .m4v, .webm, .flv, .wmv, .avi, .mov, .mp4");
        break;
      default:
        break;
    }

    if (!ok) return;

    // clear any old errors
    setImageError("");
    setMultiImagesError("");
    setQrImageError("");
    setAudioError("");
    setVideoError("");
    setLogoError("");

    if (isUpdate) {
      setUpdateQR((prev) => ({
        ...prev,
        ...(fieldName === "multi_images"
          ? { new_multi_images: [...(Array.isArray(prev.new_multi_images) ? prev.new_multi_images : []), ...files] }
          : { [fieldName]: file }),
        ...(fieldName === "qr_image" ? { remove_qr_image: false } : {}),
      }));
    } else {
      setNewQR((prev) => ({
        ...prev,
        [fieldName]: fieldName === "multi_images"
          ? [...(Array.isArray(prev.multi_images) ? prev.multi_images : []), ...files]
          : file,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // basic required fields (you can relax them if not required)
    if (
      !newQR.title.trim() ||
      !newQR.title_ar.trim()
    ) {
      setAddMsg({ ok: "", err: "Title fields (EN/AR) are required." });
      setTimeout(() => setAddMsg({ ok: "", err: "" }), 5000);
      return;
    }

    // block on extension errors
    if (imageError || multiImagesError || qrImageError || audioError || videoError || logoError) return;

    const formData = new FormData();
    formData.append("title", newQR.title);
    formData.append("description", newQR.description);
    formData.append("qr_code_url", newQR.qr_code_url);
    formData.append("name", newQR.name);
    formData.append("title_ar", newQR.title_ar);
    formData.append("description_ar", newQR.description_ar);
    formData.append("name_ar", newQR.name_ar);
    formData.append("link_url_en", newQR.link_url_en || "");
    formData.append("link_url_ar", newQR.link_url_ar || "");
    formData.append("embed_video_url", newQR.embed_video_url || "");

    if (newQR.image) formData.append("image", newQR.image);
    if (newQR.audio) formData.append("audio", newQR.audio);
    if (newQR.video) formData.append("video", newQR.video);
    if (newQR.logo) formData.append("logo", newQR.logo);
    if (Array.isArray(newQR.multi_images)) {
      newQR.multi_images.forEach((file) => formData.append("multi_images", file));
    }
    if (newQR.qr_image) formData.append("qr_image", newQR.qr_image);

    try {
      await axios.post(`${API_BASE_URL}/qr/add`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAddMsg({ ok: "QR added successfully.", err: "" });

      // reset
      setNewQR({ ...emptyQRState });
      // refresh list
      fetchData();
      setTimeout(() => setAddMsg({ ok: "", err: "" }), 4000);
    } catch (err) {
      console.error("Error adding QR:", err);
      setAddMsg({ ok: "", err: "Error adding QR. Please try again." });
    }
  };

  // ====== handlers: update (نفس الموجود عندك تقريباً) ======
  const handleUpdate = (qr) => {
    setSelectedQRId(qr.id);
    setUpdateQR(buildUpdateQRState(qr));
    setShowUpdateForm(true);
  };

  const removeExistingUpdateImage = (imagePathToRemove) => {
    setUpdateQR((prev) => ({
      ...prev,
      existing_multi_images: (prev.existing_multi_images || []).filter((imagePath) => imagePath !== imagePathToRemove),
    }));
  };

  const clearAllExistingUpdateImages = () => {
    setUpdateQR((prev) => ({
      ...prev,
      existing_multi_images: [],
    }));
  };

  const removePendingUpdateImage = (indexToRemove) => {
    setUpdateQR((prev) => ({
      ...prev,
      new_multi_images: (prev.new_multi_images || []).filter((_, index) => index !== indexToRemove),
    }));
  };

  const clearAllPendingUpdateImages = () => {
    setUpdateQR((prev) => ({
      ...prev,
      new_multi_images: [],
    }));
  };

  const removeCurrentQrImage = () => {
    setUpdateQR((prev) => ({
      ...prev,
      existing_qr_image: "",
      qr_image: null,
      remove_qr_image: true,
    }));
  };

  const clearSelectedQrImage = () => {
    setUpdateQR((prev) => ({
      ...prev,
      qr_image: null,
    }));
  };

  const resetUpdateForm = () => {
    setShowUpdateForm(false);
    setSelectedQRId(null);
    setUpdateQR({ ...emptyQRState });
    setImageError("");
    setMultiImagesError("");
    setQrImageError("");
    setAudioError("");
    setVideoError("");
    setLogoError("");
    setUpdateError({ titleError: "", descriptionError: "", nameError: "", generalError: "" });
    setUpdateQRSuccessMessage("");
  };

  const handleQRUpdate = async (e) => {
    e.preventDefault();
    if (imageError || multiImagesError || qrImageError || audioError || videoError || logoError) return;

    setUpdateError({ titleError: "", descriptionError: "", nameError: "", generalError: "" });

    let hasError = false;
    if (!updateQR.title.trim()) {
      setUpdateError((p) => ({ ...p, titleError: "Title is required." }));
      hasError = true;
    }
    // if (!updateQR.name.trim()) {
    //   setUpdateError((p) => ({ ...p, nameError: "Name is required." }));
    //   hasError = true;
    // }
    if (!updateQR.title_ar.trim()) {
      setUpdateError((p) => ({ ...p, generalError: "Arabic title is required." }));
      hasError = true;
    }
    if (hasError) return;

    const formData = new FormData();
    formData.append("title", updateQR.title);
    formData.append("description", updateQR.description);
    formData.append("qr_code_url", updateQR.qr_code_url);
    formData.append("name", updateQR.name);
    formData.append("title_ar", updateQR.title_ar);
    formData.append("description_ar", updateQR.description_ar);
    formData.append("name_ar", updateQR.name_ar);
    formData.append("link_url_en", updateQR.link_url_en || "");
    formData.append("link_url_ar", updateQR.link_url_ar || "");
    formData.append("embed_video_url", updateQR.embed_video_url || "");
    formData.append("existing_multi_images", JSON.stringify(updateQR.existing_multi_images || []));
    formData.append("remove_qr_image", updateQR.remove_qr_image ? "1" : "0");

    if (updateQR.image instanceof File) formData.append("image", updateQR.image);
    if (updateQR.audio instanceof File) formData.append("audio", updateQR.audio);
    if (updateQR.video instanceof File) formData.append("video", updateQR.video);
    if (updateQR.logo instanceof File) formData.append("logo", updateQR.logo);
    if (Array.isArray(updateQR.new_multi_images) && updateQR.new_multi_images.length) {
      updateQR.new_multi_images.forEach((file) => formData.append("multi_images", file));
    }
    if (updateQR.qr_image instanceof File) formData.append("qr_image", updateQR.qr_image);

    try {
      const res = await axios.put(`${API_BASE_URL}/qr/update/${selectedQRId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.status === 200) {
        setUpdateQRSuccessMessage("QR code updated successfully");
        setUpdateQR(buildUpdateQRState(res.data.data || {}));
        fetchData();
        setTimeout(() => setUpdateQRSuccessMessage(""), 4000);
      }
    } catch (err) {
      console.error("Error updating QR:", err);
      setUpdateError((p) => ({ ...p, generalError: "Update failed." }));
    }
  };

  // ====== data ======
  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/qr/list`);
      setQRcodes(res.data.data || []);
      setQRCodeCount((res.data.data || []).length);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error(err);
      setLoading(false);
      setError("Error fetching data.");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const renderUpdateForm = () => (
    <Card style={{ marginBottom: 0 }}>
      <CardHeader>
        <CardTitle tag="h4">Update QR</CardTitle>
      </CardHeader>
      <CardBody>
        <Form onSubmit={handleQRUpdate}>
          <Row>
            <Col md="6">
              <FormGroup>
                <label htmlFor="upd_title_en">Title:</label>
                <Input
                  id="upd_title_en"
                  name="title"
                  type="text"
                  value={updateQR.title}
                  onChange={(e) => setUpdateQR({ ...updateQR, title: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label htmlFor="upd_title_ar">Arabic Title:</label>
                <Input
                  id="upd_title_ar"
                  name="title_ar"
                  type="text"
                  dir="rtl"
                  value={updateQR.title_ar}
                  onChange={(e) => setUpdateQR({ ...updateQR, title_ar: e.target.value })}
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <label htmlFor="upd_desc_en">Description:</label>
                <textarea
                  id="upd_desc_en"
                  name="description"
                  className="form-control"
                  style={{ minHeight: 120 }}
                  value={updateQR.description}
                  onChange={(e) => setUpdateQR({ ...updateQR, description: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label htmlFor="upd_desc_ar">Arabic Description:</label>
                <textarea
                  id="upd_desc_ar"
                  name="description_ar"
                  className="form-control"
                  style={{ minHeight: 120 }}
                  dir="rtl"
                  value={updateQR.description_ar}
                  onChange={(e) => setUpdateQR({ ...updateQR, description_ar: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <label htmlFor="upd_link_url_en">Link EN (Optional):</label>
                <Input
                  id="upd_link_url_en"
                  name="link_url_en"
                  type="url"
                  value={updateQR.link_url_en}
                  onChange={(e) => setUpdateQR({ ...updateQR, link_url_en: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <label htmlFor="upd_link_url_ar">Link AR (Optional):</label>
                <Input
                  id="upd_link_url_ar"
                  name="link_url_ar"
                  type="url"
                  value={updateQR.link_url_ar}
                  onChange={(e) => setUpdateQR({ ...updateQR, link_url_ar: e.target.value })}
                  dir="rtl"
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <label htmlFor="upd_embed_video_url">Embed Video URL (Optional):</label>
                <Input
                  id="upd_embed_video_url"
                  name="embed_video_url"
                  type="url"
                  value={updateQR.embed_video_url}
                  onChange={(e) => setUpdateQR({ ...updateQR, embed_video_url: e.target.value })}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <label htmlFor="upd_multi_images">Multi Images (add/remove, optional):</label>
                {Array.isArray(updateQR.existing_multi_images) && updateQR.existing_multi_images.length ? (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {updateQR.existing_multi_images.map((imagePath, index) => (
                        <div
                          key={`upd-existing-multi-${index}`}
                          style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                        >
                          <img
                            src={resolveMediaUrl(imagePath)}
                            alt={`Multi ${index + 1}`}
                            style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: 8 }}
                          />
                          <Button
                            color="danger"
                            size="sm"
                            type="button"
                            style={{ marginTop: "6px" }}
                            onClick={() => removeExistingUpdateImage(imagePath)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {!updateQR.existing_multi_images.length ? (
                  <div className="text-muted mb-2">No current images.</div>
                ) : null}
                {Array.isArray(updateQR.new_multi_images) && updateQR.new_multi_images.length ? (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <strong>New Images To Add</strong>
                      <Button color="secondary" size="sm" type="button" onClick={clearAllPendingUpdateImages}>
                        Clear Selected
                      </Button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                      {updateQR.new_multi_images.map((file, index) => (
                        <div key={`upd-new-multi-${index}`} style={{ border: "1px solid #e6e6e6", borderRadius: 10, padding: 10 }}>
                          <div className="text-muted" style={{ fontSize: 13, marginBottom: 8, wordBreak: "break-word" }}>
                            {file.name}
                          </div>
                          <Button color="danger" size="sm" type="button" block onClick={() => removePendingUpdateImage(index)}>
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                <Input id="upd_multi_images" type="file" multiple onChange={(e) => handleFileChange(e, "multi_images", true)} />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <label htmlFor="upd_qr_image">QR Code Image (replace/remove, optional):</label>
                {updateQR.existing_qr_image ? (
                  <div style={{ marginBottom: 10 }}>
                    <img
                      src={resolveMediaUrl(updateQR.existing_qr_image)}
                      alt="QR Code"
                      style={{ maxWidth: "200px", borderRadius: 8, display: "block", marginBottom: 10 }}
                    />
                    <Button color="secondary" size="sm" type="button" onClick={() => downloadFile(updateQR.existing_qr_image)}>
                      Download Current
                    </Button>
                  </div>
                ) : null}
                {!updateQR.existing_qr_image && updateQR.remove_qr_image ? (
                  <div className="text-muted mb-2">Current QR image will be removed after update.</div>
                ) : null}
                {updateQR.qr_image instanceof File ? (
                  <div style={{ marginBottom: 10 }}>
                    <div className="text-muted mb-2">Selected file: {updateQR.qr_image.name}</div>
                    <Button color="secondary" size="sm" type="button" onClick={clearSelectedQrImage}>
                      Clear Selected
                    </Button>
                  </div>
                ) : null}
                <Input id="upd_qr_image" type="file" onChange={(e) => handleFileChange(e, "qr_image", true)} />
              </FormGroup>
            </Col>
          </Row>

          {imageError && <div style={{ color: "red" }}>{imageError}</div>}
          {multiImagesError && <div style={{ color: "red" }}>{multiImagesError}</div>}
          {qrImageError && <div style={{ color: "red" }}>{qrImageError}</div>}
          {logoError && <div style={{ color: "red" }}>{logoError}</div>}
          {audioError && <div style={{ color: "red" }}>{audioError}</div>}
          {videoError && <div style={{ color: "red" }}>{videoError}</div>}
          {updateQRSuccessMessage && (
            <div className="alert alert-success" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
              {updateQRSuccessMessage}
            </div>
          )}
          {updateError.titleError && (
            <div className="alert alert-danger" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
              {updateError.titleError}
            </div>
          )}
          {updateError.descriptionError && (
            <div className="alert alert-danger" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
              {updateError.descriptionError}
            </div>
          )}
          {updateError.nameError && (
            <div className="alert alert-danger" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
              {updateError.nameError}
            </div>
          )}
          {updateError.generalError && (
            <div className="alert alert-danger" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
              {updateError.generalError}
            </div>
          )}

          <div className="d-flex gap-2">
            <Button className="btn-round" color="primary" type="submit">
              Update QR Code
            </Button>
            <Button
              className="btn-round"
              color="secondary"
              type="button"
              onClick={resetUpdateForm}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );

  return (
    <>
      {/* ====== NEW: Add QR Form ====== */}
      <div className="p-5 mt-5">
        {/* <Card>
          <CardHeader>
            <CardTitle tag="h4">Add QR</CardTitle>
          </CardHeader>
          <CardBody>
            <Form onSubmit={handleSubmit}>
              <Row> */}
                  {/* <Col md="6">
                  <FormGroup>
                    <label htmlFor="name">Name (EN):</label>
                    <Input id="name" name="name" type="text" value={newQR.name} onChange={handleAddInputChange} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label htmlFor="name_ar">الاسم (AR):</label>
                    <Input id="name_ar" name="name_ar" type="text" value={newQR.name_ar} onChange={handleAddInputChange} dir="rtl" />
                  </FormGroup>
                </Col> */}
                {/* <Col md="6">
                  <FormGroup>
                    <label htmlFor="title">Title (EN):</label>
                    <Input id="title" name="title" type="text" value={newQR.title} onChange={handleAddInputChange} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label htmlFor="title_ar">العنوان (AR):</label>
                    <Input id="title_ar" name="title_ar" type="text" value={newQR.title_ar} onChange={handleAddInputChange} dir="rtl" />
                  </FormGroup>
                </Col> */}

              

                {/* <Col md="12">
                  <FormGroup>
                    <label htmlFor="qr_code_url">QR Code URL (اختياري):</label>
                    <Input id="qr_code_url" name="qr_code_url" type="text" value={newQR.qr_code_url} onChange={handleAddInputChange} />
                  </FormGroup>
                </Col> */}

                {/* <Col md="6">
                  <FormGroup>
                    <label htmlFor="description">Description (EN):</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-control"
                      style={{ minHeight: 140 }}
                      value={newQR.description}
                      onChange={handleAddInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label htmlFor="description_ar">الوصف (AR):</label>
                    <textarea
                      id="description_ar"
                      name="description_ar"
                      className="form-control"
                      style={{ minHeight: 140 }}
                      dir="rtl"
                      value={newQR.description_ar}
                      onChange={handleAddInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <label htmlFor="link_url_en">Link EN (Optional):</label>
                    <Input
                      id="link_url_en"
                      name="link_url_en"
                      type="url"
                      value={newQR.link_url_en}
                      onChange={handleAddInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <label htmlFor="link_url_ar">Link AR (Optional):</label>
                    <Input
                      id="link_url_ar"
                      name="link_url_ar"
                      type="url"
                      value={newQR.link_url_ar}
                      onChange={handleAddInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <label htmlFor="embed_video_url">Embed Video URL (Optional):</label>
                    <Input
                      id="embed_video_url"
                      name="embed_video_url"
                      type="url"
                      value={newQR.embed_video_url}
                      onChange={handleAddInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label htmlFor="multi_images">Multi Images (Optional):</label>
                    <Input
                      id="multi_images"
                      type="file"
                      multiple
                      onChange={(e) => handleFileChange(e, "multi_images", false)}
                    />
                    {Array.isArray(newQR.multi_images) && newQR.multi_images.length ? (
                      <div className="text-muted mt-2">{newQR.multi_images.length} files selected</div>
                    ) : null}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label htmlFor="qr_image">QR Code Image (Optional):</label>
                    <Input
                      id="qr_image"
                      type="file"
                      onChange={(e) => handleFileChange(e, "qr_image", false)}
                    />
                  </FormGroup>
                </Col>
              </Row> */}

              {/* <Row>
                <Col md="3">
                  <FormGroup>
                    <label htmlFor="image">Image:</label>
                    <Input id="image" type="file" onChange={(e) => handleFileChange(e, "image", false)} />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label htmlFor="audio">Audio:</label>
                    <Input id="audio" type="file" onChange={(e) => handleFileChange(e, "audio", false)} />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label htmlFor="video">Video:</label>
                    <Input id="video" type="file" onChange={(e) => handleFileChange(e, "video", false)} />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label htmlFor="logo">Logo:</label>
                    <Input id="logo" type="file" onChange={(e) => handleFileChange(e, "logo", false)} />
                  </FormGroup>
                </Col>
              </Row> */}

              {/* {imageError && <div style={{ color: "red" }}>{imageError}</div>}
              {multiImagesError && <div style={{ color: "red" }}>{multiImagesError}</div>}
              {qrImageError && <div style={{ color: "red" }}>{qrImageError}</div>}
              {audioError && <div style={{ color: "red" }}>{audioError}</div>}
              {videoError && <div style={{ color: "red" }}>{videoError}</div>}
              {logoError && <div style={{ color: "red" }}>{logoError}</div>}
              {addMsg.ok && (
                <div className="alert alert-success" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                  {addMsg.ok}
                </div>
              )}
              {addMsg.err && (
                <div className="alert alert-danger" style={{ width: "50%", margin: "10px auto", textAlign: "center" }}>
                  {addMsg.err}
                </div>
              )}

              <Button color="primary" className="btn-round" type="submit">
                Add QR
              </Button>
            </Form>
          </CardBody>
        </Card> */}
      </div>

      {/* ====== List ====== */}
      <div className="p-5 mt-0">
        <Card>
          <CardHeader>
            <CardTitle tag="h4">QR Code List</CardTitle>
          </CardHeader>
          <CardFooter>
            <div>Total QR Codes: {qrCodeCount}</div>
          </CardFooter>
          <CardBody>
            <Table responsive>
              <thead className="text-primary">
                <tr>
                  {/* <th style={{ width: "100px" }}>Name</th>
                  <th style={{ width: "100px" }}>Arabic Name</th> */}
                  <th style={{ width: "80px" }}>Title</th>
                  <th style={{ width: "80px" }}>Arabic Title</th>
                  <th style={{ width: "200px" }}>Description</th>
                  <th style={{ width: "200px" }}>Arabic Description</th>
                  <th style={{ width: "180px" }}>Multi Images</th>
                  <th style={{ width: "140px" }}>Link EN</th>
                  <th style={{ width: "140px" }}>Link AR</th>
                  <th style={{ width: "160px" }}>Embed Video</th>
                  <th style={{ width: "160px" }}>QR Image</th>
                  {/* <th style={{ width: "100px" }}>Image</th>
                  <th style={{ width: "100px" }}>Audio</th>
                  <th style={{ width: "100px" }}>Video</th>
                  <th style={{ width: "100px" }}>Logo</th> */}
                  <th style={{ width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {qrcodes.map((qr) => (
                  <Fragment key={qr.id}>
                    <tr>
                      {/* <td style={{ width: "100px" }}>{qr.name}</td>
                      <td style={{ width: "100px" }}>{qr.name_ar || "No Arabic Name"}</td> */}
                      <td style={{ width: "80px" }}>{qr.title}</td>
                      <td style={{ width: "80px" }}>{qr.title_ar || "No Arabic Title"}</td>
                      <td style={{ width: "200px" }}>
                        {qr.description?.length > 30 ? (
                          <>
                            {qr.description.slice(0, 30)}{" "}
                            <Button color="primary" onClick={() => toggleDescription(qr.id)} className="btn-sm mt-2" style={{ fontSize: "9px" }}>
                              Read More
                            </Button>
                          </>
                        ) : (
                          qr.description
                        )}
                      </td>
                      <td style={{ width: "200px" }}>
                        {qr.description_ar
                          ? qr.description_ar.length > 30
                            ? (
                              <>
                                {qr.description_ar.slice(0, 30)}{" "}
                                <Button color="primary" onClick={() => toggleDescription(qr.id)} className="btn-sm mt-2" style={{ fontSize: "9px" }}>
                                  Read More
                                </Button>
                              </>
                              )
                            : qr.description_ar
                          : "No Arabic Description"}
                      </td>
                      <td style={{ width: "180px" }}>
                        {Array.isArray(qr.multi_images) && qr.multi_images.length ? (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {qr.multi_images.slice(0, 3).map((imagePath, index) => (
                              <img
                                key={`${qr.id}-multi-${index}`}
                                src={resolveMediaUrl(imagePath)}
                                alt={`QR Gallery ${index + 1}`}
                                style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }}
                              />
                            ))}
                            {qr.multi_images.length > 3 ? (
                              <span className="text-muted">+{qr.multi_images.length - 3}</span>
                            ) : null}
                          </div>
                        ) : (
                          "No Images"
                        )}
                      </td>
                      <td style={{ width: "140px" }}>
                        {qr.link_url_en ? (
                          <a href={qr.link_url_en} target="_blank" rel="noopener noreferrer">
                            Open Link
                          </a>
                        ) : (
                          "No Link"
                        )}
                      </td>
                      <td style={{ width: "140px" }}>
                        {qr.link_url_ar ? (
                          <a href={qr.link_url_ar} target="_blank" rel="noopener noreferrer">
                            Open Link
                          </a>
                        ) : (
                          "No Link"
                        )}
                      </td>
                      <td style={{ width: "160px" }}>
                        {qr.embed_video_url ? (
                          <a href={qr.embed_video_url} target="_blank" rel="noopener noreferrer">
                            View Video
                          </a>
                        ) : (
                          "No Video"
                        )}
                      </td>
                      <td style={{ width: "160px" }}>
                        {qr.qr_image ? (
                          <div>
                            <img
                              src={resolveMediaUrl(qr.qr_image)}
                              alt="QR Upload"
                              style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, display: "block", marginBottom: 8 }}
                            />
                            <Button color="secondary" size="sm" onClick={() => downloadFile(qr.qr_image)}>
                              Download
                            </Button>
                          </div>
                        ) : (
                          "No QR Image"
                        )}
                      </td>
                      {/* <td style={{ width: "100px" }}>
                        {qr.image && <img src={`${API_BASE_URL}${qr.image}`} alt="QR" style={{ maxWidth: "100%" }} />}
                      </td>
                      <td style={{ width: "100px" }}>
                        {qr.audio && (
                          <audio controls>
                            <source src={`${API_BASE_URL}${qr.audio}`} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                        )}
                      </td>
                      <td style={{ width: "100px" }}>
                        {qr.video && (
                          <video controls width="100%">
                            <source src={`${API_BASE_URL}${qr.video}`} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </td>
                      <td style={{ width: "100px" }}>
                        {qr.logo && <img src={`${API_BASE_URL}${qr.logo}`} alt="Logo" style={{ maxWidth: "100%" }} />}
                      </td> */}
                      <td style={{ width: "100px" }}>
                        <Button color="success" onClick={() => handleUpdate(qr)}>
                          Update
                        </Button>
                      </td>
                    </tr>
                    {expandedDescriptionId === qr.id && (
                      <tr>
                        <td colSpan="10">
                          <div>
                            <p>{qr.description}</p>
                            <p dir="rtl">{qr.description_ar}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {showUpdateForm && selectedQRId === qr.id && (
                      <tr>
                        <td colSpan="10" style={{ padding: "16px 0" }}>
                          {renderUpdateForm()}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

export default QRManagement;
